import React, { useMemo } from 'react';
import './CardTextDisplay.css';
import { htmlSeguro } from '@/lib/htmlSeguro';
import faceFrances from '@/assets/frances.png';
import faceBrasil from '@/assets/brasil.png';

interface PalavraComCor {
  palabra: string;
  cor: string;
  corFundo?: string; // Cor de fundo opcional
  colado?: boolean; // true = sem espaço antes desta palavra (grudada na anterior)
  negrito?: boolean;
  sublinhado?: boolean;
}

interface CardTextDisplayProps {
  textoFrances: PalavraComCor[] | string;
  alphanat: PalavraComCor[] | string;
  traducaoPortugues: string;
  textoTraduzido?: string;
  imagemPrincipal?: string;
  audioPath?: string;
  className?: string;
  step?: number;      // step 1, 2, or 3
  hideAudio?: boolean; // se true, oculta o player nativo
}

/**
 * Componente para exibir card com texto formatado colorido
 * 
 * Layout:
 * - Imagem principal (se houver)
 * - Linha 1: Texto francês com fundo #333399 e palavras coloridas flowing naturally
 * - Linha 2: Alphanat (transcrição fonética) com fundo #fefdd5 e palavras coloridas flowing naturally
 * - Tradução em português (texto simples)
 * - Texto traduzido (como francês falaria em PT) se houver
 * - Player de áudio (se houver)
 */
const CardTextDisplay: React.FC<CardTextDisplayProps> = ({
  textoFrances,
  alphanat,
  traducaoPortugues,
  textoTraduzido,
  imagemPrincipal,
  audioPath,
  className = '',
  step = 3,
  hideAudio = false
}) => {
  // A tradução é HTML guardado no banco, e este é o card que o ALUNO vê. O que
  // vai para a tela é só a formatação que um card usa (ver lib/htmlSeguro).
  const traducaoSegura = useMemo(() => htmlSeguro(traducaoPortugues), [traducaoPortugues]);
  const textoTraduzidoSeguro = useMemo(() => htmlSeguro(textoTraduzido), [textoTraduzido]);

  // Helper para normalizar caminhos de mídia (imagem e áudio)
  const getMediaUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
      return path;
    }
    const apiBase = import.meta.env.VITE_API_URL || '/api';
    // Garante que não duplica barra inclinada
    const cleanBase = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
  };

  // Helper para converter string ou array em PalavraComCor[] de forma segura
  const normalize = (val: any): PalavraComCor[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim() !== '') {
      // Tenta fazer parse se for string JSON
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // Se falhar, trata como texto plano e divide por espaços
      }
      return val.split(/\s+/).map(word => ({ palabra: word, cor: '#000000' }));
    }
    return [];
  };

  const safeTextoFrances = normalize(textoFrances);
  const safeAlphanat = normalize(alphanat);

  // Agrupa fragmentos "colados" (sem espaço entre eles no Word, ex: "cô" +
  // "mmôn_") em uma única palavra/bloco. Cada grupo = 1 palavra real,
  // podendo ter internamente vários pedaços de cores diferentes.
  const agruparPalavras = (items: PalavraComCor[]): PalavraComCor[][] => {
    const grupos: PalavraComCor[][] = [];
    items.forEach(item => {
      if (item.colado && grupos.length > 0) {
        grupos[grupos.length - 1].push(item);
      } else {
        grupos.push([item]);
      }
    });
    return grupos;
  };

  const gruposFrances = agruparPalavras(safeTextoFrances);
  const gruposAlphanat = agruparPalavras(safeAlphanat);

  // Pareamento por coluna: quando o nº de palavras (já agrupadas) do
  // francês e do alphanat bate, renderizamos cada par em uma coluna
  // (palavra em cima, alphanat embaixo) — igual à estrutura alinhada do
  // Word. Caso contrário, caímos no modo antigo (duas linhas separadas).
  // NUNCA altera os dados do card.
  const podeAlinharColunas =
    gruposFrances.length > 0 &&
    gruposFrances.length === gruposAlphanat.length;

  const FRENCH_BG = '#333399';
  const ALPHANAT_BG = '#fefdd5';

  const showImage = !!imagemPrincipal;
  const showTextGrid = step >= 2;
  const showTranslation = step >= 3;

  // Renderiza os pedaços de uma palavra, cada um com sua cor de texto E sua
  // cor de fundo (highlight vindo do Word). Sem isso, uma letra branca sobre
  // fundo colorido ficaria invisível.
  // forcedColor: se informado, ignora a cor gravada do texto (usado no
  // francês, que é sempre branco sobre o azul — igual ao Word).
  // A palavra é o grupo, e os pedaços dela vêm colados: o respiro do fundo vai
  // só nas pontas. Com respiro em cada pedaço, "su"+"ar" virava "su ar" na
  // tela — um vão no meio da palavra que o Word não tem.
  const renderSegs = (grupo: PalavraComCor[], forcedColor?: string) =>
    grupo.map((seg, i) => (
      <span
        key={i}
        style={{
          color: forcedColor || seg.cor,
          backgroundColor: seg.corFundo || 'transparent',
          ...(seg.negrito ? { fontWeight: 700 } : {}),
          ...(seg.sublinhado ? { textDecoration: 'underline' } : {}),
          ...(seg.corFundo ? {
            paddingLeft: i === 0 ? 2 : 0,
            paddingRight: i === grupo.length - 1 ? 2 : 0,
            borderRadius: `${i === 0 ? 3 : 0}px ${i === grupo.length - 1 ? 3 : 0}px ${i === grupo.length - 1 ? 3 : 0}px ${i === 0 ? 3 : 0}px`,
          } : {}),
        }}
      >
        {seg.palabra}
      </span>
    ));

  return (
    <div className={`card-text-display ${className}`}>
      {/* Imagem Principal */}
      {showImage && imagemPrincipal && (
        <div className="card-image-container">
          <img 
            src={getMediaUrl(imagemPrincipal)} 
            alt="Imagem do card" 
            className="card-main-image"
          />
        </div>
      )}

      {/* Container de Texto Formatado */}
      {showTextGrid && (safeTextoFrances.length > 0 || safeAlphanat.length > 0) && (
        <div className="card-text-container">

          {/* Modo COLUNAS: alphanat exatamente embaixo de cada palavra */}
          {podeAlinharColunas && (
            <div className="paired-rows">
              {gruposFrances.map((frGrupo, index) => {
                const alGrupo = gruposAlphanat[index];
                const columnWeight = Math.max(
                  frGrupo.reduce((total, segmento) => total + segmento.palabra.length, 0),
                  alGrupo.reduce((total, segmento) => total + segmento.palabra.length, 0),
                  2
                );
                return (
                  <div
                    className="word-column"
                    key={`pair-${index}`}
                    style={{ '--column-weight': columnWeight } as React.CSSProperties}
                  >
                    <span className="pair-cell pair-french" style={{ backgroundColor: FRENCH_BG }}>
                      {renderSegs(frGrupo, '#ffffff')}
                    </span>
                    <span className="pair-cell pair-alphanat" style={{ backgroundColor: ALPHANAT_BG }}>
                      {renderSegs(alGrupo)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Modo LINHAS (fallback): usado quando as contagens não batem */}
          {!podeAlinharColunas && safeTextoFrances.length > 0 && (
            <div className="text-row french-text" style={{ backgroundColor: '#333399' }}>
              <div className="text-sentence">
                {gruposFrances.map((grupo, index) => (
                  <React.Fragment key={`french-${index}`}>
                    {index > 0 ? ' ' : ''}
                    <span className="word-span">{renderSegs(grupo, '#ffffff')}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Linha 2: Alphanat (fundo amarelo claro) */}
          {!podeAlinharColunas && safeAlphanat.length > 0 && (
            <div className="text-row alphanat-text" style={{ backgroundColor: '#fefdd5' }}>
              <div className="text-sentence">
                {gruposAlphanat.map((grupo, index) => (
                  <React.Fragment key={`alpha-${index}`}>
                    {index > 0 ? ' ' : ''}
                    <span className="word-span">{renderSegs(grupo)}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tradução em Português */}
      {showTranslation && (
        <div className="translation-section">
          <div className="translation-label">Tradução:</div>
          <div className="translation-row">
            <img src={faceFrances} alt="" className="line-face-inline" aria-hidden="true" />
            <div className="translation-text" dangerouslySetInnerHTML={{ __html: traducaoSegura }} />
          </div>
        </div>
      )}

      {/* Texto Traduzido (opcional) */}
      {showTranslation && textoTraduzido && (
        <div className="translated-text-section">
          <div className="translated-label">Como se fala em PT:</div>
          <div className="translation-row">
            <img src={faceBrasil} alt="" className="line-face-inline" aria-hidden="true" />
            <div className="translated-text" dangerouslySetInnerHTML={{ __html: textoTraduzidoSeguro }} />
          </div>
        </div>
      )}

      {/* Player de Áudio */}
      {!hideAudio && audioPath && (
        <div className="audio-section">
          <audio controls className="audio-player">
            <source src={getMediaUrl(audioPath)} type="audio/mpeg" />
            <source src={getMediaUrl(audioPath)} type="audio/wav" />
            Seu navegador não suporta o elemento de áudio.
          </audio>
        </div>
      )}
    </div>
  );
};

export default CardTextDisplay;
